import { Message, PendingRequest } from "./types";

export class H5Bridge {
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private requestCounter: number = 0;
  private readonly REQUEST_TIMEOUT: number = 15000;

  constructor() {
    this.initMessageListener();
  }

  // 初始化消息监听
  private initMessageListener(): void {
    const handleMessage = (event: MessageEvent): void => {
      this.handleMessage(event);
    };

    // 兼容不同环境
    document.addEventListener("message", handleMessage as EventListener);
    window.addEventListener("message", handleMessage);
  }

  // 生成唯一请求ID
  private generateRequestId(funcId: string): string {
    const timestamp = Date.now();
    const counter = ++this.requestCounter;
    const random = Math.random().toString(36).substr(2, 6);
    return `H5_${funcId}_${timestamp}_${counter}_${random}`;
  }

  // 发送消息到RN
  private postMessage(message: Message): void {
    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    } else {
      console.error("ReactNativeWebView not available");
    }
  }

  // 模式2: H5→RN 无返回值
  public notify(funcId: string, data?: any): void {
    const message: Message = {
      type: "NOTIFY",
      funcId,
      source: "H5",
      needResponse: false,
      data,
      timestamp: Date.now(),
    };
    this.postMessage(message);
  }

  // 模式4: H5→RN 有返回值
  public call<T = any>(funcId: string, data?: any, timeoutMs: number = this.REQUEST_TIMEOUT): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const requestId = this.generateRequestId(funcId);

      // 设置超时处理
      const timeout = window.setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Request timeout: ${funcId}`));
      }, timeoutMs);

      // 保存待处理请求
      this.pendingRequests.set(requestId, {
        resolve,
        reject,
        timeout,
      });

      // 发送请求消息
      const message: Message = {
        type: "REQUEST",
        funcId,
        requestId,
        source: "H5",
        needResponse: true,
        data,
        timestamp: Date.now(),
      };

      this.postMessage(message);
    });
  }

  // 处理来自RN的消息
  public handleMessage(event: MessageEvent): void {
    try {
      const message: Message = JSON.parse(event.data);

      switch (message.type) {
        case "REQUEST":
          this.handleRequest(message);
          break;
        case "RESPONSE":
          this.handleResponse(message);
          break;
        case "NOTIFY":
          this.handleNotify(message);
          break;
        default:
          console.warn("Unknown message type:", message.type);
      }
    } catch (error) {
      console.error("Failed to parse message:", error);
    }
  }

  // 处理RN发来的请求（模式3）
  private async handleRequest(message: Message): Promise<void> {
    const { funcId, requestId, data } = message;

    // 暂不需处理通知
    console.warn(`No handler registered for notify: ${funcId} ${data}`);
    this.postMessage({
      type: "RESPONSE",
      funcId,
      requestId,
      source: "H5",
      error: `Function not found: ${funcId}`,
      timestamp: Date.now(),
    });
  }

  // 处理RN发来的响应（模式4的响应）
  private handleResponse(message: Message): void {
    const { requestId, data, error } = message;

    if (!requestId) return;

    const pendingRequest = this.pendingRequests.get(requestId);
    if (!pendingRequest) return;

    // 清理超时定时器
    clearTimeout(pendingRequest.timeout);
    this.pendingRequests.delete(requestId);

    if (error) {
      pendingRequest.reject(new Error(error));
    } else {
      pendingRequest.resolve(data);
    }
  }

  // 处理RN发来的通知（模式1）
  private handleNotify(message: Message): void {
    const { funcId, data } = message;

    // 暂不需处理通知
    console.warn(`No handler registered for notify: ${funcId} ${data}`);
  }

  // 清理资源
  public destroy(): void {
    // 清理所有待处理请求
    this.pendingRequests.forEach(({ timeout, reject }) => {
      clearTimeout(timeout);
      reject(new Error("Bridge destroyed"));
    });
    this.pendingRequests.clear();
  }
}

// 创建全局实例
const createBridge = (): H5Bridge => {
  return new H5Bridge();
};

// 导出实例
export const bridge = createBridge();

// 挂载到全局对象
if (typeof window !== "undefined") {
  window.RNBridge = bridge;
}
